// SPDX-License-Identifier: Elastic-2.0
/**
 * Per-route placeholder config table (F-330 T-007).
 *
 * Single source of truth for "coming soon" routes shipped as part of
 * the sidebar IA refresh. The router (T-010) iterates this table to
 * register lazy `<ComingSoonPage config={…} />` routes — one code
 * path for every destination.
 *
 * Schema:
 *   - `route`            — the URL path the placeholder serves. Sourced
 *                          from `ROUTES` so a rename can't drift.
 *   - `icon`             — name of an entry in `iconPaths.ts`. Typed as
 *                          `IconName` so a typo fails the build.
 *   - `title_i18n`       — i18n key for the page title (resolved at
 *                          render time, falls back to the key in EN).
 *   - `description_i18n` — i18n key for the body paragraph.
 *
 * See: specs/wip/feat-37-sidebar-ia-refresh/technical/sidebar-ia-refresh.md § 4.1
 * See: specs/wip/feat-37-sidebar-ia-refresh/functional/sidebar-ia-refresh.md § 5
 */
import type { IconName } from "../Icon/Icon";

export interface PlaceholderConfig {
  /** Route this placeholder serves. Used by the router wiring + tests. */
  route: string;
  /** Big icon shown above the title. */
  icon: IconName;
  /** i18n key for the page title (e.g. "placeholder.devices.title"). */
  title_i18n: string;
  /** i18n key for the description paragraph. */
  description_i18n: string;
}

// Intentionally empty as of F-340: all 11 F-330 sidebar destinations
// have shipped as real pages (/devices F-341, /account F-331, /plan
// F-332, /homes F-336, /remote-access F-333, /voice F-335, /backups
// F-334, /api-keys F-337, /publish F-339, /marketplace F-338, and
// /analytics F-340). The table — and the ComingSoonPage component —
// are kept in place so future features can re-add a placeholder
// without re-introducing the plumbing.
export const PLACEHOLDERS: readonly PlaceholderConfig[] = [] as const;
